import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiLock,
  FiEye,
  FiEyeOff,
  FiLogIn,
  FiShield,
  FiArrowLeft
} from 'react-icons/fi';
import { authService } from '../../services/auth';
import './Login.css';

interface LoginProps {
  setIsAuthenticated: (isAuth: boolean) => void;
  setUser: (user: any) => void;
}

const Login = ({ setIsAuthenticated, setUser }: LoginProps) => {
  const navigate = useNavigate();
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // 2FA — controlado pela resposta do backend (requires2FA)
  const [show2FA, setShow2FA] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const twoFactorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show2FA) {
      twoFactorInputRef.current?.focus();
    }
  }, [show2FA]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await authService.login(
        emailOrUsername,
        password,
        twoFactorCode || undefined
      );

      if (result.ok) {
        const user = authService.getUser();
        setIsAuthenticated(true);
        setUser(user);

        if (user?.role === 'admin') {
          navigate('/admin');
        } else {
          navigate('/dashboard');
        }
        return;
      }

      if (result.requires2FA) {
        setShow2FA(true);
        setError(
          twoFactorCode
            ? result.message || 'Código de verificação inválido.'
            : result.message || 'Sua conta possui verificação em duas etapas. Informe o código gerado no autenticador.'
        );
        return;
      }

      setError(result.message || 'Usuário/Email ou senha inválidos.');
    } catch (err) {
      setError('Erro ao tentar fazer login. Tente novamente.');
      console.error('Erro no login:', err);
    } finally {
      setLoading(false);
    }
  };

  const handle2FAChange = (value: string) => {
    const onlyDigits = value.replace(/\D/g, '').slice(0, 6);
    setTwoFactorCode(onlyDigits);
  };

  const handleBackFrom2FA = () => {
    setShow2FA(false);
    setTwoFactorCode('');
    setError('');
  };

  return (
    <div className="login-page">
      <div className="login-bg">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
        <div className="grid-overlay" />
      </div>

      <div className="login-container">
        <div className="logo-container">
          <div className="logo-glow" />
          <img
            src="https://digitalsac.io/wp-content/uploads/2024/02/digitalsac2026.png"
            alt="DigitalSac"
          />
        </div>

        <h1 className="brand-title">GetCID</h1>
        <p className="brand-subtitle">
          {show2FA ? 'Verificação em duas etapas' : 'Sistema de Consulta de CID'}
        </p>

        <form onSubmit={handleSubmit} className="login-form">
          {!show2FA && (
            <>
              <div className="input-group">
                <label htmlFor="emailOrUsername">Email ou Usuário</label>
                <div className="input-wrapper">
                  <FiUser className="input-icon" aria-hidden="true" />
                  <input
                    type="text"
                    id="emailOrUsername"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    placeholder="seu email ou usuário"
                    autoComplete="username"
                    required
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="input-group">
                <label htmlFor="password">Senha</label>
                <div className="input-wrapper">
                  <FiLock className="input-icon" aria-hidden="true" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    required
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((s) => !s)}
                    aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                    tabIndex={-1}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </button>
                </div>
              </div>
            </>
          )}

          {show2FA && (
            <div className="input-group twofa-group">
              <p className="twofa-intro">
                <FiShield aria-hidden="true" />
                <span>
                  Olá, <strong>{emailOrUsername}</strong>. Informe o código de 6 dígitos
                  gerado pelo seu app autenticador.
                </span>
              </p>
              <label htmlFor="twoFactorCode">Código de verificação</label>
              <div className="input-wrapper">
                <input
                  ref={twoFactorInputRef}
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  maxLength={6}
                  id="twoFactorCode"
                  value={twoFactorCode}
                  onChange={(e) => handle2FAChange(e.target.value)}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  required
                  disabled={loading}
                  className="twofa-input"
                />
              </div>
            </div>
          )}

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <button type="submit" className="submit-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner" />
                {show2FA ? 'Verificando...' : 'Entrando...'}
              </>
            ) : (
              <>
                <FiLogIn />
                {show2FA ? 'Verificar e entrar' : 'Entrar'}
              </>
            )}
          </button>

          {show2FA && (
            <button
              type="button"
              className="back-btn"
              onClick={handleBackFrom2FA}
              disabled={loading}
            >
              <FiArrowLeft />
              Voltar
            </button>
          )}
        </form>

        <div className="login-info">
          <p className="company">DigitalSac Software Engineering</p>
          <a
            href="https://digitalsac.io"
            target="_blank"
            rel="noopener noreferrer"
            className="company-link"
          >
            digitalsac.io
          </a>
        </div>
      </div>
    </div>
  );
};

export default Login;
